import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Send, MoreHorizontal } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { api, formatApiError } from "@/lib/api";
import { StatusPill, EmptyState, TableWrap, th, td, fmtDate } from "@/components/dashboard/ui";

export default function LabelTeam({ onChange }) {
  const [team, setTeam] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ email: "", display_name: "", label_role: "member" });
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, i] = await Promise.all([api.get("/label/team"), api.get("/label/invitations")]);
      setTeam(t.data);
      setInvites(i.data.filter((x) => x.role === "label"));
    } catch (e) {
      toast.error("Failed to load team");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const send = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) {
      toast.error("Enter an email address");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/label/invitations", { ...form, kind: "team", app_url: window.location.origin });
      toast.success(data.email_sent ? `Seat invite emailed to ${form.email}` : `Seat invite created for ${form.email}`);
      setForm({ email: "", display_name: "", label_role: "member" });
      load();
      onChange && onChange();
    } catch (err) {
      toast.error(formatApiError(err, "Failed to invite"));
    } finally {
      setSending(false);
    }
  };

  const revoke = async (id) => {
    try {
      await api.delete(`/label/invitations/${id}`);
      toast.success("Invitation revoked");
      load();
      onChange && onChange();
    } catch (err) {
      toast.error("Failed to revoke");
    }
  };

  const removeMember = async (memberId) => {
    try {
      await api.delete(`/label/team/${memberId}`);
      toast.success("Team member removed");
      load();
      onChange && onChange();
    } catch (err) {
      toast.error(formatApiError(err, "Failed to remove"));
    }
  };

  const inputCls = "w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 placeholder:text-slate-500";

  return (
    <div className="space-y-8" data-testid="label-team">
      <form onSubmit={send} className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="label-invite-team-form">
        <h3 className="font-display text-lg text-white mb-4">Add a team member</h3>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <input data-testid="team-email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="teammate@email.com" type="email" className={`md:col-span-4 ${inputCls}`} />
          <input data-testid="team-name" value={form.display_name} onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))} placeholder="Name (optional)" className={`md:col-span-3 ${inputCls}`} />
          <div className="md:col-span-3">
            <Select value={form.label_role} onValueChange={(v) => setForm((f) => ({ ...f, label_role: v }))}>
              <SelectTrigger data-testid="team-role" className="bg-white/5 border-white/10 text-white h-[50px] rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0b1220] border-white/10 text-white">
                <SelectItem value="member">Member</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <button data-testid="team-submit" type="submit" disabled={sending} className="md:col-span-2 inline-flex items-center justify-center gap-2 rounded-xl bg-white text-black font-medium px-5 py-3 hover:bg-slate-200 disabled:opacity-60 transition-all">
            {sending ? <Loader2 size={18} className="animate-spin" /> : <><Send size={16} /> Invite</>}
          </button>
        </div>
      </form>

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-slate-500" /></div>
      ) : (
        <>
          <div>
            <h3 className="font-display text-lg text-white mb-4">Team</h3>
            <TableWrap>
              <thead>
                <tr>
                  <th className={th}>Member</th>
                  <th className={th}>Seat</th>
                  <th className={th}>Status</th>
                  <th className={th}></th>
                </tr>
              </thead>
              <tbody>
                {team.map((m) => (
                  <tr key={m.user_id} data-testid={`team-row-${m.user_id}`} className="hover:bg-white/[0.02]">
                    <td className={td}>
                      <div className="text-white">{m.display_name || m.name}</div>
                      <div className="text-xs text-slate-500">{m.email}</div>
                    </td>
                    <td className={td + " capitalize"}>{m.label_role}</td>
                    <td className={td}><StatusPill status={m.status} /></td>
                    <td className={td + " text-right"}>
                      {m.label_role !== "owner" && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button data-testid={`team-actions-${m.user_id}`} className="p-2 rounded-lg hover:bg-white/10 text-slate-400"><MoreHorizontal size={18} /></button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-[#0b1220] border-white/10 text-white">
                            <DropdownMenuItem onClick={() => removeMember(m.user_id)} className="text-red-400 focus:bg-white/10 focus:text-red-400 cursor-pointer" data-testid={`team-remove-${m.user_id}`}>
                              Remove from team
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          </div>

          {invites.length > 0 && (
            <div>
              <h3 className="font-display text-lg text-white mb-4">Pending seat invites</h3>
              <TableWrap>
                <thead>
                  <tr>
                    <th className={th}>Email</th>
                    <th className={th}>Seat</th>
                    <th className={th}>Status</th>
                    <th className={th}>Sent</th>
                    <th className={th}></th>
                  </tr>
                </thead>
                <tbody>
                  {invites.map((inv) => (
                    <tr key={inv.invitation_id} data-testid={`seat-invite-row-${inv.invitation_id}`} className="hover:bg-white/[0.02]">
                      <td className={td + " text-white"}>{inv.email}</td>
                      <td className={td + " capitalize"}>{inv.label_role || "member"}</td>
                      <td className={td}><StatusPill status={inv.status} /></td>
                      <td className={td}>{fmtDate(inv.created_at)}</td>
                      <td className={td + " text-right"}>
                        {inv.status === "pending" && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button data-testid={`seat-invite-actions-${inv.invitation_id}`} className="p-2 rounded-lg hover:bg-white/10 text-slate-400"><MoreHorizontal size={18} /></button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-[#0b1220] border-white/10 text-white">
                              <DropdownMenuItem onClick={() => revoke(inv.invitation_id)} className="text-red-400 focus:bg-white/10 focus:text-red-400 cursor-pointer" data-testid={`seat-invite-revoke-${inv.invitation_id}`}>
                                Revoke invitation
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </TableWrap>
            </div>
          )}
        </>
      )}
    </div>
  );
}
